import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Cog, QrCode, Pencil, Trash2, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, machinePhotoSrc, API } from "../lib/api";
import { STATUS_LABEL, STATUS_STYLES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import SummaryTab from "../components/passport/SummaryTab";
import DocumentsTab from "../components/passport/DocumentsTab";
import HistoryTab from "../components/passport/HistoryTab";
import PhotosTab from "../components/passport/PhotosTab";

export default function MachinePassport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [machine, setMachine] = useState(null);
  const [error, setError] = useState("");
  const [qrOpen, setQrOpen] = useState(false);

  useEffect(() => {
    setMachine(null);
    api.get(`/machines/${id}`).then(({ data }) => setMachine(data)).catch((e) => setError(fmtErr(e)));
  }, [id]);

  const handleDelete = async () => {
    try {
      await api.delete(`/machines/${id}`);
      toast.success("Máquina eliminada");
      navigate("/app");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const handlePrintQr = () => {
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(
      `<html><head><title>QR - ${machine.name}</title></head><body style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:95vh;margin:0;font-family:sans-serif;"><img src="${API}/machines/${id}/qr" style="width:320px"/><p style="margin:16px 0 0;font-size:18px;font-weight:600;max-width:90%;text-align:center;overflow-wrap:break-word">${machine.name}</p><p style="margin:6px 0 0;font-size:11px;letter-spacing:0.15em;color:#a1a1aa">assetpassport.io</p><script>window.onload=()=>window.print()<\/script></body></html>`
    );
    win.document.close();
  };

  if (error) {
    return (
      <div className="text-center py-24" data-testid="machine-error">
        <p className="text-zinc-500">{error}</p>
        <Link to="/app" className="text-sm text-zinc-900 font-medium underline underline-offset-4 mt-3 inline-block">Volver a máquinas</Link>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
      </div>
    );
  }

  const photo = machinePhotoSrc(machine);
  const status = STATUS_STYLES[machine.status] || STATUS_STYLES.operativa;

  return (
    <div className="animate-fade-up" data-testid="machine-passport">
      <Link
        to="/app"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 transition-colors mb-6"
        data-testid="back-to-machines"
      >
        <ArrowLeft size={15} /> Máquinas
      </Link>

      <div className="grid lg:grid-cols-[minmax(0,380px),minmax(0,1fr)] gap-8 lg:gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <div className="relative rounded-3xl overflow-hidden bg-zinc-100 aspect-[4/3] card-shadow" data-testid="passport-photo">
            {photo ? (
              <img src={photo} alt={machine.name} className="h-full w-full object-cover" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-zinc-300">
                <Cog size={64} strokeWidth={1} />
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
            <span
              className={`absolute bottom-4 left-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md bg-white/85 border-0 ${status.badge}`}
              data-testid="passport-status"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
              {STATUS_LABEL[machine.status] || machine.status}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter mt-6" data-testid="passport-name">
            {machine.name}
          </h1>
          <p className="text-zinc-500 mt-1" data-testid="passport-model">
            {[machine.manufacturer, machine.model].filter(Boolean).join(" · ") || "—"}
          </p>

          <div className="mt-5 space-y-2.5 text-sm">
            {machine.serial_number && (
              <p className="text-zinc-600" data-testid="passport-serial">
                <span className="text-zinc-400">S/N:</span> {machine.serial_number}
              </p>
            )}
            {machine.supplier && (
              <p className="text-zinc-600" data-testid="passport-supplier">
                <span className="text-zinc-400">Proveedor:</span> {machine.supplier}
              </p>
            )}
            {machine.service_application && (
              <p className="text-zinc-600" data-testid="passport-service-application">
                <span className="text-zinc-400">Servicio / aplicación:</span> {machine.service_application}
              </p>
            )}
            {machine.location_name && (
              <p className="flex items-center gap-1.5 text-zinc-600" data-testid="passport-location">
                <MapPin size={15} className="text-zinc-400" />
                {machine.location_name}
              </p>
            )}
            {machine.year && (
              <p className="text-zinc-600" data-testid="passport-year">
                <span className="text-zinc-400">Año:</span> {machine.year}
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 mt-7">
            {isAdmin && (
              <Link
                to={`/app/machines/${id}/edit`}
                data-testid="edit-machine-button"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
              >
                <Pencil size={15} /> Editar máquina
              </Link>
            )}
            <button
              onClick={() => setQrOpen(true)}
              data-testid="show-qr-button"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-zinc-200 text-sm font-medium hover:border-zinc-300 active:scale-95 transition-[border-color,transform] duration-200"
            >
              <QrCode size={15} /> Código QR
            </button>
          </div>

          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  className="mt-5 text-sm text-zinc-400 hover:text-rose-600 transition-colors inline-flex items-center gap-1.5"
                  data-testid="delete-machine-button"
                >
                  <Trash2 size={14} /> Eliminar máquina
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-3xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar esta máquina?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se eliminarán también sus documentos, fotos e historial. Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full" data-testid="delete-machine-cancel">Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    className="rounded-full bg-rose-600 hover:bg-rose-700"
                    data-testid="delete-machine-confirm"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        <div className="min-w-0">
          <Tabs defaultValue="resumen">
            <TabsList className="bg-zinc-100 rounded-full p-1 h-auto w-full sm:w-auto grid grid-cols-4 sm:inline-flex" data-testid="passport-tabs">
              <TabsTrigger value="resumen" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2.5 text-sm" data-testid="tab-resumen">Resumen</TabsTrigger>
              <TabsTrigger value="documentos" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2.5 text-sm" data-testid="tab-documentos">Documentos</TabsTrigger>
              <TabsTrigger value="historial" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2.5 text-sm" data-testid="tab-historial">Historial</TabsTrigger>
              <TabsTrigger value="fotos" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2.5 text-sm" data-testid="tab-fotos">Fotos</TabsTrigger>
            </TabsList>
            <div className="mt-7">
              <TabsContent value="resumen" className="mt-0"><SummaryTab machine={machine} /></TabsContent>
              <TabsContent value="documentos" className="mt-0"><DocumentsTab machine={machine} isAdmin={isAdmin} /></TabsContent>
              <TabsContent value="historial" className="mt-0"><HistoryTab machine={machine} user={user} /></TabsContent>
              <TabsContent value="fotos" className="mt-0"><PhotosTab machine={machine} isAdmin={isAdmin} /></TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="rounded-3xl sm:max-w-sm" data-testid="qr-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">QR de {machine.name}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center py-4">
            <img
              src={`${API}/machines/${id}/qr`}
              alt={`QR de ${machine.name}`}
              className="w-56 h-56 rounded-2xl border border-zinc-200"
              data-testid="qr-image"
            />
            <p className="text-xs text-zinc-400 mt-4 text-center max-w-xs">
              Imprime este código y colócalo sobre la máquina. Al escanearlo se abrirá directamente su pasaporte.
            </p>
            <div className="flex gap-3 mt-6 w-full">
              <a
                href={`${API}/machines/${id}/qr`}
                download={`qr-${machine.name.replace(/\s+/g, "-").toLowerCase()}.png`}
                data-testid="qr-download-button"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
              >
                <Download size={15} /> Descargar
              </a>
              <button
                onClick={handlePrintQr}
                data-testid="qr-print-button"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-white border border-zinc-200 text-sm font-medium hover:border-zinc-300 active:scale-95 transition-[border-color,transform] duration-200"
              >
                <Printer size={15} /> Imprimir
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
